import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { xano, type HsSuggestion, type HsSuggestionItem } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import {
  PageHeader,
  StatCard,
  SectionTitle,
  DataTable,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, ChevronDown, ChevronRight, Building2 } from "lucide-react";
import { gbp, num, pct } from "@/lib/format";
import type { OverviewPayload, MatrixCell, Product } from "@/lib/engine-types";

const confVariant = (c?: string): "success" | "warning" | "secondary" => {
  if (c === "high") return "success";
  if (c === "medium") return "warning";
  return "secondary";
};
const confRank = (c?: string) => (c === "high" ? 0 : c === "medium" ? 1 : 2);
const sortByConfidence = (s: HsSuggestionItem[]) =>
  [...(s ?? [])].sort((a, b) => confRank(a.confidence) - confRank(b.confidence));

export default function Catalogue() {
  const { activeToken, tokenLoading } = useCompany();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<number | null>(null);

  const overview = useQuery({
    queryKey: ["overview", activeToken],
    queryFn: () => xano.dashboard.overview(activeToken!) as Promise<OverviewPayload>,
    enabled: !!activeToken,
  });
  const suggestions = useQuery({
    queryKey: ["hs-suggestions", activeToken],
    queryFn: () => xano.products.suggestions(activeToken!),
    enabled: !!activeToken,
  });

  const suggestMut = useMutation({
    mutationFn: (productId: number) => xano.products.suggestHs(activeToken!, productId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hs-suggestions", activeToken] }),
  });

  const byProduct = useMemo(() => {
    const m = new Map<number, HsSuggestion>();
    (suggestions.data?.suggestions ?? []).forEach((s) => m.set(s.product_id, s));
    return m;
  }, [suggestions.data]);

  if (!activeToken && !tokenLoading) {
    return (
      <>
        <PageHeader title="Catalogue" />
        <EmptyState icon={Building2} title="No company yet" description="Connect a store to load products." />
      </>
    );
  }
  if (tokenLoading || overview.isLoading) {
    return (
      <>
        <PageHeader title="Catalogue" />
        <LoadingState />
      </>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <>
        <PageHeader title="Catalogue" />
        <ErrorState error={overview.error} />
      </>
    );
  }

  const d = overview.data;
  const products = d.products ?? [];
  const matrix = d.matrix ?? [];
  const dests = d.matrix_destinations ?? [];
  const withHs = products.filter((p) => p.hs_code && p.hs_code.length > 0).length;
  const missing = products.length - withHs;
  const aiCount = byProduct.size;

  const cell = (pid: string, dest: string): MatrixCell | undefined =>
    matrix.find(
      (m) => String(m.shopify_product_id) === String(pid) && m.destination_country_code === dest
    );

  return (
    <>
      <PageHeader
        title="Catalogue"
        description="Your Shopify products, HS codes (set vs missing), AI classification and the landed-cost matrix."
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Products" value={num(products.length)} />
        <StatCard label="HS codes set" value={num(withHs)} tone="success" />
        <StatCard
          label="HS codes missing"
          value={num(missing)}
          tone={missing > 0 ? "warning" : "success"}
        />
        <StatCard label="AI classified" value={num(aiCount)} hint="Suggested by GoEcom" />
      </div>

      <div className="mb-3 mt-6 flex items-center justify-between">
        <SectionTitle>Products</SectionTitle>
        <span className="text-xs text-muted-foreground">
          HS codes power duties at checkout, customs forms and the landed-cost engine.
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 text-left font-medium">Product</th>
                  <th className="px-4 py-2.5 text-left font-medium">SKU</th>
                  <th className="px-4 py-2.5 text-left font-medium">HS code</th>
                  <th className="px-4 py-2.5 text-left font-medium">AI suggestion</th>
                  <th className="px-4 py-2.5 text-right font-medium">Price</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p: Product) => {
                  const sug = byProduct.get(p.id);
                  const isOpen = expanded === p.id;
                  const busy = suggestMut.isPending && suggestMut.variables === p.id;
                  return (
                    <ProductRow
                      key={p.id}
                      product={p}
                      suggestion={sug}
                      isOpen={isOpen}
                      busy={busy}
                      onSuggest={() => suggestMut.mutate(p.id)}
                      onToggle={() => setExpanded(isOpen ? null : p.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {matrix.length > 0 && (
        <>
          <SectionTitle>Landed-cost matrix</SectionTitle>
          <p className="-mt-2 mb-3 text-sm text-muted-foreground">
            Total landed cost (duty + import VAT + shipping) per product, per destination, with the
            uplift over the product price.
          </p>
          <DataTable
            columns={[
              { key: "title", label: "Product" },
              ...dests.map((dc) => ({ key: dc, label: dc, align: "right" as const })),
            ]}
            rows={products.map((p) => {
              const row: Record<string, React.ReactNode> = {
                title: <span className="font-medium">{p.title}</span>,
              };
              for (const dc of dests) {
                const c = cell(String(p.shopify_product_id ?? ""), dc);
                row[dc] = c ? (
                  <div>
                    <div className="font-medium">{gbp(c.total_landed, { decimals: true })}</div>
                    {c.product_value > 0 && c.total_landed > c.product_value && (
                      <div className="text-xs text-muted-foreground">
                        +{pct(((c.total_landed - c.product_value) / c.product_value) * 100, 0)}
                      </div>
                    )}
                  </div>
                ) : (
                  "—"
                );
              }
              return row;
            })}
          />
        </>
      )}
    </>
  );
}

function ProductRow({
  product,
  suggestion,
  isOpen,
  busy,
  onSuggest,
  onToggle,
}: {
  product: Product;
  suggestion?: HsSuggestion;
  isOpen: boolean;
  busy: boolean;
  onSuggest: () => void;
  onToggle: () => void;
}) {
  const items = suggestion ? sortByConfidence(suggestion.suggestions) : [];
  const top = items[0];
  return (
    <>
      <tr className="border-b last:border-0 hover:bg-muted/20">
        <td className="px-4 py-2.5 font-medium">{product.title}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{product.sku || "—"}</td>
        <td className="px-4 py-2.5">
          {product.hs_code ? (
            <Badge variant="secondary">{product.hs_code}</Badge>
          ) : (
            <Badge variant="warning">missing</Badge>
          )}
        </td>
        <td className="px-4 py-2.5">
          {top ? (
            <button onClick={onToggle} className="flex items-center gap-1.5">
              <Badge variant={confVariant(top.confidence)}>
                <Sparkles className="h-3 w-3" />
                {top.short_code || top.code}
              </Badge>
              <span className="text-xs text-muted-foreground">{top.confidence}</span>
              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <Button variant="secondary" className="h-7 px-2 text-xs" disabled={busy} onClick={onSuggest}>
              <Sparkles className="h-3 w-3" /> {busy ? "Classifying…" : "Suggest HS"}
            </Button>
          )}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">{gbp(product.price, { decimals: true })}</td>
      </tr>
      {isOpen && top && (
        <tr className="border-b bg-muted/20">
          <td colSpan={5} className="px-6 py-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              GoEcom AI classification (HMRC tariff + Claude)
            </div>
            <ul className="space-y-1.5">
              {items.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Badge variant={confVariant(s.confidence)}>{s.short_code || s.code}</Badge>
                  <span className="text-muted-foreground">
                    {s.description ? s.description + " — " : ""}
                    {s.reasoning}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              className="mt-3 h-7 px-2 text-xs"
              disabled={busy}
              onClick={onSuggest}
            >
              {busy ? "Re-running…" : "Re-classify"}
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}
